'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useMounted } from '@/hooks/useMounted';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import type { MatchWithRating } from '@/components/match/types';

type TimeRange = '7d' | '30d' | '90d' | 'all';

interface RatingProgressionChartProps {
  matchHistory: MatchWithRating[];
  isOwnProfile: boolean;
}

export default function RatingProgressionChart({
  matchHistory,
  isOwnProfile,
}: RatingProgressionChartProps) {
  const t = useTranslations('Profile');
  const mounted = useMounted();
  const [range, setRange] = useState<TimeRange>('30d');

  const hasEnoughData = matchHistory.filter((m) => m.myRating !== null).length >= 2;

  const chartData = useMemo(() => {
    const withRating = matchHistory.filter((m) => m.myRating !== null).reverse();

    if (withRating.length === 0) return [];

    const now = new Date();
    const cutoff = new Date();
    switch (range) {
      case '7d':
        cutoff.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoff.setDate(now.getDate() - 30);
        break;
      case '90d':
        cutoff.setDate(now.getDate() - 90);
        break;
      case 'all':
        cutoff.setFullYear(2000);
        break;
    }

    const formatDate = (d: Date) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    const inRange = withRating.filter((m) => new Date(m.dateISO) >= cutoff);
    const beforeCutoff = withRating.filter((m) => new Date(m.dateISO) < cutoff);
    const startRating =
      beforeCutoff.length > 0
        ? Math.round(beforeCutoff[beforeCutoff.length - 1].myRating!)
        : inRange.length > 0
          ? Math.round(inRange[0].myRating!)
          : null;

    if (startRating === null) return [];

    const points: { date: string; rating: number }[] = [];

    if (range !== 'all' && beforeCutoff.length > 0) {
      points.push({ date: formatDate(cutoff), rating: startRating });
    }

    for (const m of inRange) {
      points.push({
        date: formatDate(new Date(m.dateISO)),
        rating: Math.round(m.myRating!),
      });
    }

    const lastRating = points.length > 0 ? points[points.length - 1].rating : startRating;
    const todayStr = formatDate(now);
    if (points.length === 0 || points[points.length - 1].date !== todayStr) {
      points.push({ date: todayStr, rating: lastRating });
    }

    return points;
  }, [matchHistory, range]);

  // No data and not own profile — hide completely
  if (!hasEnoughData && !isOwnProfile) {
    return null;
  }

  // No data but own profile — show "Start Your Journey" empty state
  if (!hasEnoughData) {
    return (
      <Card className="overflow-hidden">
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
          <div className="bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <svg
              className="text-primary h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-foreground mb-1 text-lg font-bold">{t('startYourJourney')}</h3>
          <p className="text-muted max-w-xs text-sm">{t('startYourJourneyDescription')}</p>
        </div>
      </Card>
    );
  }

  // Has data but current time range is empty — hide
  if (chartData.length === 0) {
    return null;
  }

  const minRating = Math.min(...chartData.map((d) => d.rating)) - 20;
  const maxRating = Math.max(...chartData.map((d) => d.rating)) + 20;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('ratingProgression')}</CardTitle>
          <Select
            className="w-auto"
            value={range}
            onChange={(e) => setRange(e.target.value as TimeRange)}
          >
            <option value="7d">{t('last7Days')}</option>
            <option value="30d">{t('last30Days')}</option>
            <option value="90d">{t('last90Days')}</option>
            <option value="all">{t('allTime')}</option>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          {mounted && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRatingProfile" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  className="fill-muted"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[minRating, maxRating]}
                  tick={{ fontSize: 11 }}
                  className="fill-muted"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-card)',
                    fontSize: '12px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="rating"
                  stroke="var(--color-primary)"
                  fillOpacity={1}
                  fill="url(#colorRatingProfile)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
